import { ok } from 'neverthrow';
import type {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { WorkflowEditorToolbarActionBase } from './workflow-editor-toolbar-action.base';
import {
  workflowCanvasShortcutAvailable,
  workflowModelFromContext,
  type WorkflowClipboardOps,
} from './workflow-toolbar-action.helpers';
import {
  pasteWorkflowSlice,
  readWorkflowClipboard,
  workflowPasteAnchor,
} from './workflow-node-clipboard';

/**
 * Pastes the copied slice into this workflow.
 *
 * The clipboard outlives the canvas that filled it, so this also covers
 * pasting into a different workflow — which is the case where the originals'
 * upstream nodes are absent and the copies land unconnected.
 */
export class WorkflowPasteNodesAction extends WorkflowEditorToolbarActionBase {
  title = 'Paste nodes';
  override description = 'Paste the copied nodes into this workflow';
  group = 'Workflow';
  icon = 'clipboard';
  shortcut = ['ctrl', 'v'];

  constructor(private readonly ops: WorkflowClipboardOps) {
    super();
  }

  shortcutAvailable(): boolean {
    return workflowCanvasShortcutAvailable(this.ops);
  }

  override applicable(context: ApplicableContext): boolean {
    return (
      workflowModelFromContext(context) !== null &&
      readWorkflowClipboard() !== null
    );
  }

  override async execute(
    _params: Record<string, never>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string }[]>> {
    const model = workflowModelFromContext(context);
    const payload = readWorkflowClipboard();
    if (model === null || payload === null) {
      return ok({});
    }
    return pasteWorkflowSlice({
      model,
      payload,
      anchor: workflowPasteAnchor(model),
      verb: 'Pasted',
    });
  }
}
